import { City } from '@/data/cities';
import { KeywordCategory, getCategoryById } from '@/data/seo/cityKeywords';

export type CityLanguage = string;

export interface SEOContent {
  h2: string;
  introduction: string;
  sections: SEOSection[];
  relatedLinks: RelatedLink[];
  citySpecificInfo: CitySpecificInfo;
}

export interface SEOSection {
  h3: string;
  content: string;
  keywords: string[];
  extendedContent?: string;
}

export interface RelatedLink {
  text: string;
  url: string;
  category: string;
}

export interface CitySpecificInfo {
  population: string;
  timezone: string;
  country: string;
  mentalHealthStats: MentalHealthStats;
}

export interface MentalHealthStats {
  prevalenceRate: string;
  mostCommonCondition: string;
  therapistsPerCapita: string;
  averageWaitTime: string;
}

/**
 * FASE 2: Enhanced content templates with more variations
 */
const CONTENT_VARIATIONS = {
  en: {
    introduction: [
      'In {city}, our comprehensive mental health network connects you with {population} residents to qualified professionals. With {therapists} therapists per capita, we offer diverse therapeutic approaches including CBT, EMDR, psychodynamic therapy, and mindfulness-based interventions.',
      'Finding the right therapist in {city} is crucial for your mental wellness journey. Our platform serves the {population} community with carefully vetted psychologists who understand the unique challenges of living in {city}, {country}.',
      'Welcome to {city}\'s premier mental health directory. Whether you\'re dealing with anxiety, depression, relationship issues, or seeking personal growth, our network of licensed professionals in {city} is here to support your {population} community with evidence-based care.',
      '{city} residents deserve accessible, quality mental health care. Our directory features therapists specializing in various modalities, all committed to serving the mental wellness needs of this vibrant community of {population} people.',
      'Mental health support in {city} has never been more accessible. Connect with experienced psychologists and therapists who understand the cultural, social, and environmental factors unique to {city}, {country}, serving {population} residents.'
    ],
    sectionContent: [
      'Our {specialty} services in {city} are designed to meet the unique needs of our {population} community. Therapists utilize evidence-based approaches proven effective for treating {condition}, with {waitTime} average wait times for appointments.',
      'In {city}, {condition} affects approximately {prevalence} of residents. Our specialized {specialty} professionals combine clinical expertise with compassionate care, offering flexible scheduling across {timezone} to accommodate your busy life.',
      'Connect with expert {specialty} therapists in {city} who understand the local context. With {therapists} mental health professionals per capita, you have access to diverse treatment approaches including individual therapy, group sessions, and online counseling.',
      'The {population} residents of {city} have access to comprehensive {specialty} services. Our network includes therapists trained in the latest therapeutic techniques, offering both in-person sessions and teletherapy options in {timezone}.',
      '{specialty} in {city} goes beyond traditional therapy. Our professionals integrate cultural sensitivity, local awareness, and proven therapeutic methods to provide personalized care for the diverse {city} community.',
      'Whether you prefer morning or evening sessions in {timezone}, {city}\'s {specialty} therapists offer flexible scheduling. With {prevalence} of the population experiencing {condition}, accessible mental health care is more important than ever.',
      'Expert {specialty} available throughout {city}, {country}. Our therapists stay current with the latest research and techniques, ensuring you receive cutting-edge care tailored to your individual needs and the {city} lifestyle.',
      'In {city}, we recognize that mental health is as important as physical health. Our {specialty} professionals provide confidential, judgment-free support with {waitTime} average wait times, making help accessible when you need it most.'
    ],
    cityAdvantages: [
      'choosing therapy in {city} means accessing a diverse pool of culturally competent professionals',
      'the mental health community in {city} is known for innovative, evidence-based approaches',
      '{city} therapists understand the unique stressors of urban/suburban living in {country}',
      'with {therapists} therapists per capita, {city} offers exceptional mental health resources',
      'convenient {timezone} scheduling makes therapy accessible for busy {city} residents',
      'both in-person and online options available throughout {city}',
      '{city}\'s mental health professionals represent diverse backgrounds and specialties',
      'many therapists in {city} offer sliding scale fees and accept major insurance plans'
    ],
    therapeuticApproaches: [
      'Cognitive Behavioral Therapy (CBT) - evidence-based approach for anxiety, depression, and more',
      'EMDR Therapy - effective for trauma processing and PTSD treatment',
      'Psychodynamic Therapy - exploring unconscious patterns and early experiences',
      'Mindfulness-Based Therapy - integrating meditation and present-moment awareness',
      'Dialectical Behavior Therapy (DBT) - skills for emotion regulation and distress tolerance',
      'Acceptance and Commitment Therapy (ACT) - building psychological flexibility',
      'Solution-Focused Brief Therapy - goal-oriented, practical approach',
      'Family Systems Therapy - understanding relational dynamics and patterns',
      'Narrative Therapy - reauthoring your life story',
      'Gestalt Therapy - focus on present awareness and personal responsibility'
    ],
    synonyms: {
      therapist: ['counselor', 'psychotherapist', 'mental health professional', 'clinician'],
      psychologist: ['clinical psychologist', 'mental health expert', 'psychological specialist'],
      therapy: ['counseling', 'psychotherapy', 'treatment', 'therapeutic services'],
      treatment: ['intervention', 'care', 'support', 'therapy'],
      mental_health: ['psychological wellbeing', 'emotional wellness', 'mental wellness'],
      anxiety: ['worry', 'nervousness', 'anxious feelings', 'stress response'],
      depression: ['low mood', 'depressive symptoms', 'melancholy', 'sadness']
    }
  },
  es: {
    introduction: [
      'En {city}, nuestra red integral de salud mental conecta a {population} residentes con profesionales cualificados. Con {therapists} terapeutas per cápita, ofrecemos diversos enfoques terapéuticos incluyendo TCC, EMDR, terapia psicodinámica e intervenciones basadas en mindfulness.',
      'Encontrar el terapeuta adecuado en {city} es crucial para tu bienestar mental. Nuestra plataforma sirve a la comunidad de {population} con psicólogos cuidadosamente verificados que comprenden los desafíos únicos de vivir en {city}, {country}.',
      'Bienvenido al directorio premier de salud mental de {city}. Ya sea que estés lidiando con ansiedad, depresión, problemas de pareja o buscando crecimiento personal, nuestra red de profesionales certificados en {city} está aquí para apoyar a tu comunidad de {population} personas con atención basada en evidencia.',
      'Los residentes de {city} merecen atención de salud mental accesible y de calidad. Nuestro directorio presenta terapeutas especializados en diversas modalidades, todos comprometidos a servir las necesidades de bienestar mental de esta vibrante comunidad de {population} personas.',
      'El apoyo de salud mental en {city} nunca ha sido más accesible. Conéctate con psicólogos y terapeutas experimentados que comprenden los factores culturales, sociales y ambientales únicos de {city}, {country}, sirviendo a {population} residentes.'
    ],
    sectionContent: [
      'Nuestros servicios de {specialty} en {city} están diseñados para satisfacer las necesidades únicas de nuestra comunidad de {population}. Los terapeutas utilizan enfoques basados en evidencia comprobadamente efectivos para tratar {condition}, con {waitTime} tiempos de espera promedio para citas.',
      'En {city}, {condition} afecta aproximadamente a {prevalence} de los residentes. Nuestros profesionales especializados en {specialty} combinan experiencia clínica con atención compasiva, ofreciendo horarios flexibles en {timezone} para acomodar tu vida ocupada.',
      'Conéctate con terapeutas expertos en {specialty} en {city} que comprenden el contexto local. Con {therapists} profesionales de salud mental per cápita, tienes acceso a diversos enfoques de tratamiento incluyendo terapia individual, sesiones grupales y consejería online.',
      'Los {population} residentes de {city} tienen acceso a servicios integrales de {specialty}. Nuestra red incluye terapeutas capacitados en las últimas técnicas terapéuticas, ofreciendo sesiones presenciales y opciones de teleterapia en {timezone}.',
      '{specialty} en {city} va más allá de la terapia tradicional. Nuestros profesionales integran sensibilidad cultural, conciencia local y métodos terapéuticos probados para proporcionar atención personalizada a la diversa comunidad de {city}.',
      'Ya sea que prefieras sesiones matutinas o vespertinas en {timezone}, los terapeutas de {specialty} de {city} ofrecen horarios flexibles. Con {prevalence} de la población experimentando {condition}, la atención de salud mental accesible es más importante que nunca.',
      '{specialty} experto disponible en todo {city}, {country}. Nuestros terapeutas se mantienen actualizados con las últimas investigaciones y técnicas, asegurando que recibas atención de vanguardia adaptada a tus necesidades individuales y al estilo de vida de {city}.',
      'En {city}, reconocemos que la salud mental es tan importante como la salud física. Nuestros profesionales de {specialty} proporcionan apoyo confidencial y sin juicios con {waitTime} tiempos de espera promedio, haciendo la ayuda accesible cuando más la necesitas.'
    ],
    cityAdvantages: [
      'elegir terapia en {city} significa acceder a un grupo diverso de profesionales culturalmente competentes',
      'la comunidad de salud mental en {city} es conocida por sus enfoques innovadores basados en evidencia',
      'los terapeutas de {city} comprenden los estresores únicos de la vida urbana/suburbana en {country}',
      'con {therapists} terapeutas per cápita, {city} ofrece recursos excepcionales de salud mental',
      'la programación conveniente en {timezone} hace que la terapia sea accesible para residentes ocupados de {city}',
      'opciones presenciales y online disponibles en todo {city}',
      'los profesionales de salud mental de {city} representan diversos orígenes y especialidades',
      'muchos terapeutas en {city} ofrecen tarifas de escala móvil y aceptan los principales planes de seguro'
    ],
    therapeuticApproaches: [
      'Terapia Cognitivo-Conductual (TCC) - enfoque basado en evidencia para ansiedad, depresión y más',
      'Terapia EMDR - efectiva para el procesamiento de trauma y tratamiento de TEPT',
      'Terapia Psicodinámica - explorando patrones inconscientes y experiencias tempranas',
      'Terapia Basada en Mindfulness - integrando meditación y conciencia del momento presente',
      'Terapia Dialéctica Conductual (TDC) - habilidades para regulación emocional y tolerancia al malestar',
      'Terapia de Aceptación y Compromiso (ACT) - construyendo flexibilidad psicológica',
      'Terapia Breve Centrada en Soluciones - enfoque práctico orientado a objetivos',
      'Terapia de Sistemas Familiares - comprendiendo dinámicas y patrones relacionales',
      'Terapia Narrativa - reescribiendo tu historia de vida',
      'Terapia Gestalt - enfoque en la conciencia presente y responsabilidad personal'
    ],
    synonyms: {
      terapeuta: ['consejero', 'psicoterapeuta', 'profesional de salud mental', 'clínico'],
      psicólogo: ['psicólogo clínico', 'experto en salud mental', 'especialista psicológico'],
      terapia: ['consejería', 'psicoterapia', 'tratamiento', 'servicios terapéuticos'],
      tratamiento: ['intervención', 'atención', 'apoyo', 'terapia'],
      salud_mental: ['bienestar psicológico', 'bienestar emocional', 'bienestar mental'],
      ansiedad: ['preocupación', 'nerviosismo', 'sentimientos ansiosos', 'respuesta al estrés'],
      depresión: ['estado de ánimo bajo', 'síntomas depresivos', 'melancolía', 'tristeza']
    }
  }
};

/**
 * Generate mock mental health statistics for a city
 */
const generateMentalHealthStats = (city: City): MentalHealthStats => {
  // Generate realistic-looking stats based on city population
  const popSize = city.population;
  
  const prevalenceRates = ['15-20%', '18-22%', '12-18%', '20-25%'];
  const commonConditions = ['anxiety disorders', 'depression', 'stress-related conditions', 'adjustment disorders'];
  const therapistRatios = popSize > 1000000 ? '1:800' : popSize > 100000 ? '1:1200' : '1:1800';
  const waitTimes = popSize > 1000000 ? '1-2 weeks' : popSize > 100000 ? '2-3 weeks' : '1-3 weeks';
  
  return {
    prevalenceRate: prevalenceRates[Math.floor(Math.random() * prevalenceRates.length)],
    mostCommonCondition: commonConditions[Math.floor(Math.random() * commonConditions.length)],
    therapistsPerCapita: therapistRatios,
    averageWaitTime: waitTimes
  };
};

/**
 * Format city with full location (City, State, Country)
 */
export const formatCityFullName = (city: City): string => {
  const cityName = city.name.replace(/\s+\d+$/, '').trim();
  const state = city.state_province;
  
  // If has state, use it; otherwise just country
  if (state) {
    return `${cityName}, ${state}, ${city.country}`;
  }
  return `${cityName}, ${city.country}`;
};

/**
 * Format city for meta title with length awareness
 */
export const formatCityForMetaTitle = (
  city: City, 
  baseTitleLength: number,
  maxLength: number = 60
): string => {
  const cityName = city.name.replace(/\s+\d+$/, '').trim();
  const state = city.state_province;
  const country = city.country;
  
  // Calculate available space
  const availableSpace = maxLength - baseTitleLength;
  
  // Format: "City, State, Country"
  const fullFormat = state 
    ? `${cityName}, ${state}, ${country}`
    : `${cityName}, ${country}`;
  
  // If fits, use full format
  if (fullFormat.length <= availableSpace) {
    return fullFormat;
  }
  
  // If doesn't fit, use "City, State" or "City, Country"
  const shortFormat = state 
    ? `${cityName}, ${state}`
    : `${cityName}, ${country}`;
  
  if (shortFormat.length <= availableSpace) {
    return shortFormat;
  }
  
  // Last resort: just city name
  return cityName;
};

/**
 * Format population for display
 */
const formatPopulation = (population: number): string => {
  if (population >= 1000000) {
    return `${(population / 1000000).toFixed(1)}M`;
  } else if (population >= 1000) {
    return `${(population / 1000).toFixed(0)}K`;
  }
  return population.toString();
};

/**
 * Apply semantic variations to content
 */
const applySynonyms = (content: string, language: CityLanguage): string => {
  const isEnglish = language === 'en';
  const synonyms = isEnglish ? CONTENT_VARIATIONS.en.synonyms : CONTENT_VARIATIONS.es.synonyms;
  
  let variedContent = content;
  
  // Randomly replace some instances with synonyms (about 30% of occurrences)
  Object.entries(synonyms).forEach(([original, replacements]) => {
    const regex = new RegExp(`\\b${original}\\b`, 'gi');
    const matches = content.match(regex);
    
    if (matches) {
      matches.forEach((match) => {
        if (Math.random() < 0.3) {
          const synonym = replacements[Math.floor(Math.random() * replacements.length)];
          variedContent = variedContent.replace(match, synonym);
        }
      });
    }
  });
  
  return variedContent;
};

/**
 * FASE 2: Enhanced category content generation
 */
export const generateCategoryContent = (
  category: KeywordCategory,
  city: City,
  language: CityLanguage
): SEOContent => {
  const cityName = city.name.replace(/\s+\d+$/, '').trim();
  const isEnglish = language === 'en';
  
  const mentalHealthStats = generateMentalHealthStats(city);
  const populationFormatted = formatPopulation(city.population);
  
  // Select a random introduction template for variation
  const introTemplates = isEnglish ? CONTENT_VARIATIONS.en.introduction : CONTENT_VARIATIONS.es.introduction;
  const introTemplate = introTemplates[Math.floor(Math.random() * introTemplates.length)];
  
  const h2 = isEnglish 
    ? category.h2Templates.en.replace('{city}', cityName)
    : category.h2Templates.es.replace('{city}', cityName);
  
  // Enhanced introduction with city-specific data
  const introduction = introTemplate
    .replace(/{city}/g, cityName)
    .replace(/{population}/g, populationFormatted)
    .replace(/{country}/g, city.country)
    .replace(/{timezone}/g, city.timezone)
    .replace(/{therapists}/g, mentalHealthStats.therapistsPerCapita)
    .replace(/{prevalence}/g, mentalHealthStats.prevalenceRate)
    .replace(/{waitTime}/g, mentalHealthStats.averageWaitTime);
  
  const h3Templates = isEnglish ? category.h3Templates.en : category.h3Templates.es;
  const contentTemplates = isEnglish ? CONTENT_VARIATIONS.en.sectionContent : CONTENT_VARIATIONS.es.sectionContent;
  
  // Generate sections with enhanced content
  const sections: SEOSection[] = h3Templates.map((h3, index) => {
    const baseTemplate = contentTemplates[index % contentTemplates.length];
    
    let content = baseTemplate
      .replace(/{city}/g, cityName)
      .replace(/{specialty}/g, h3.toLowerCase())
      .replace(/{population}/g, populationFormatted)
      .replace(/{country}/g, city.country)
      .replace(/{timezone}/g, city.timezone)
      .replace(/{condition}/g, mentalHealthStats.mostCommonCondition)
      .replace(/{prevalence}/g, mentalHealthStats.prevalenceRate)
      .replace(/{therapists}/g, mentalHealthStats.therapistsPerCapita)
      .replace(/{waitTime}/g, mentalHealthStats.averageWaitTime);
    
    // Apply semantic variations
    content = applySynonyms(content, language);
    
    // Generate extended content with long-tail keywords
    const extendedTemplate = contentTemplates[(index + 3) % contentTemplates.length];
    const extendedContent = applySynonyms(
      extendedTemplate
        .replace(/{city}/g, cityName)
        .replace(/{specialty}/g, h3.toLowerCase())
        .replace(/{population}/g, populationFormatted)
        .replace(/{country}/g, city.country)
        .replace(/{timezone}/g, city.timezone)
        .replace(/{condition}/g, mentalHealthStats.mostCommonCondition)
        .replace(/{prevalence}/g, mentalHealthStats.prevalenceRate)
        .replace(/{therapists}/g, mentalHealthStats.therapistsPerCapita)
        .replace(/{waitTime}/g, mentalHealthStats.averageWaitTime),
      language
    );
    
    return {
      h3,
      content,
      keywords: category.keywords.slice(index * 2, (index * 2) + 2),
      extendedContent
    };
  });
  
  const relatedLinks = generateRelatedLinks(category, city, language);
  
  return {
    h2,
    introduction: applySynonyms(introduction, language),
    sections,
    relatedLinks,
    citySpecificInfo: {
      population: populationFormatted,
      timezone: city.timezone,
      country: city.country,
      mentalHealthStats
    }
  };
};

/**
 * Generate content for a specific section (legacy support)
 */
const generateSectionContent = (
  category: KeywordCategory,
  h3: string,
  cityName: string,
  language: CityLanguage
): string => {
  const isEnglish = language === 'en';
  const templates = isEnglish ? CONTENT_VARIATIONS.en.sectionContent : CONTENT_VARIATIONS.es.sectionContent;
  const template = templates[Math.floor(Math.random() * templates.length)];
  
  return template
    .replace(/{city}/g, cityName)
    .replace(/{specialty}/g, h3.toLowerCase())
    .replace(/{population}/g, '500K')
    .replace(/{country}/g, 'Country')
    .replace(/{timezone}/g, 'UTC')
    .replace(/{condition}/g, 'mental health concerns')
    .replace(/{prevalence}/g, '15-20%')
    .replace(/{therapists}/g, '1:1000')
    .replace(/{waitTime}/g, '1-2 weeks');
};

/**
 * Generate related internal links
 */
const generateRelatedLinks = (
  category: KeywordCategory,
  city: City,
  language: CityLanguage
): RelatedLink[] => {
  const citySlug = city.slug;
  const langPrefix = language === 'en' ? '' : `/${language}`;
  
  return category.relatedCategories.slice(0, 3).map(relatedId => {
    const relatedCategory = getCategoryById(relatedId);
    if (!relatedCategory) return null;
    
    const isEnglish = language === 'en';
    const categoryName = isEnglish ? relatedCategory.name : relatedCategory.nameEs;
    const cityName = city.name.replace(/\s+\d+$/, '').trim();
    
    return {
      text: isEnglish 
        ? `${categoryName} in ${cityName}`
        : `${categoryName} en ${cityName}`,
      url: `${langPrefix}/city/${citySlug}#${relatedId}`,
      category: relatedId
    };
  }).filter(Boolean) as RelatedLink[];
};

/**
 * Generate meta description optimized for SEO
 */
export const generateMetaDescription = (
  city: City,
  language: CityLanguage,
  specialtyKeywords?: string[]
): string => {
  const fullLocation = formatCityFullName(city);
  const population = formatPopulation(city.population);
  const isEnglish = language === 'en';
  
  const baseDescription = isEnglish
    ? `Connect with certified psychologists and therapists in ${fullLocation}. Serving ${population} residents with evidence-based mental health care. Online & in-person sessions available.`
    : `Conecta con psicólogos y terapeutas certificados en ${fullLocation}. Sirviendo a ${population} residentes con atención de salud mental basada en evidencia. Sesiones online y presenciales disponibles.`;
  
  if (specialtyKeywords && specialtyKeywords.length > 0) {
    const specialties = specialtyKeywords.slice(0, 3).join(', ');
    return isEnglish
      ? `${baseDescription} Specializing in ${specialties}. Book online sessions today.`
      : `${baseDescription} Especializados en ${specialties}. Reserva sesiones online hoy.`;
  }
  
  return baseDescription;
};

/**
 * FASE 2: Enhanced meta keywords with long-tail variations
 */
export const generateMetaKeywords = (
  city: City,
  language: CityLanguage,
  includeLongTail: boolean = true
): string[] => {
  const cityName = city.name.replace(/\s+\d+$/, '').trim();
  const state = city.state_province;
  const country = city.country;
  const isEnglish = language === 'en';
  
  const baseKeywords = isEnglish
    ? [
        `psychologist in ${cityName}`,
        `therapist ${cityName}`,
        ...(state ? [`therapist ${cityName} ${state}`] : []),
        `psychiatrist ${cityName}`,
        `mental health ${cityName}`,
        ...(state ? [`mental health ${state}`] : []),
        `therapy ${cityName}`,
        `counseling ${cityName} ${country}`
      ]
    : [
        `psicólogo en ${cityName}`,
        `terapeuta ${cityName}`,
        ...(state ? [`terapeuta ${cityName} ${state}`] : []),
        `psiquiatra ${cityName}`,
        `salud mental ${cityName}`,
        ...(state ? [`salud mental ${state}`] : []),
        `terapia ${cityName}`,
        `consejería ${cityName} ${country}`
      ];
  
  if (!includeLongTail) return baseKeywords;
  
  // FASE 2: Expanded long-tail keywords
  const longTailKeywords = isEnglish
    ? [
        `best psychologist ${cityName}`,
        `affordable therapy ${cityName}`,
        `online therapist ${cityName}`,
        ...(state ? [`${state} therapist directory`] : []),
        `anxiety therapist ${cityName}`,
        `depression treatment ${cityName}`,
        `couples therapy ${cityName}`,
        `child psychologist ${cityName}`,
        `EMDR therapist ${cityName}`,
        `CBT therapy ${cityName}`,
        `trauma therapy ${cityName}`,
        `therapist near me ${cityName}`,
        `top rated psychologist ${cityName}`,
        `mental health services ${cityName}`,
        `counselor in ${cityName}`,
        `psychiatric services ${cityName}`
      ]
    : [
        `mejor psicólogo en ${cityName}`,
        `terapia asequible ${cityName}`,
        `terapeuta online ${cityName}`,
        ...(state ? [`directorio terapeuta ${state}`] : []),
        `terapeuta ansiedad ${cityName}`,
        `tratamiento depresión ${cityName}`,
        `terapia pareja ${cityName}`,
        `psicólogo infantil ${cityName}`,
        `terapeuta EMDR ${cityName}`,
        `terapia TCC ${cityName}`,
        `terapia trauma ${cityName}`,
        `terapeuta cerca de mí ${cityName}`,
        `psicólogo mejor calificado ${cityName}`,
        `servicios salud mental ${cityName}`,
        `consejero en ${cityName}`,
        `servicios psiquiátricos ${cityName}`
      ];
  
  return [...baseKeywords, ...longTailKeywords];
};

/**
 * Generate H1 title optimized for SEO
 */
export const generateH1Title = (
  city: City,
  language: CityLanguage
): string => {
  const fullLocation = formatCityFullName(city);
  const isEnglish = language === 'en';
  
  return isEnglish
    ? `Professional Psychologists, Therapists & Psychiatrists in ${fullLocation}`
    : `Psicólogos, Terapeutas y Psiquiatras Profesionales en ${fullLocation}`;
};

/**
 * Generate page title for meta tags
 */
export const generatePageTitle = (
  city: City,
  language: CityLanguage
): string => {
  const isEnglish = language === 'en';
  const baseTitle = isEnglish 
    ? 'Find Therapists in'
    : 'Encuentra Terapeutas en';
  
  const cityLocation = formatCityForMetaTitle(city, baseTitle.length + 28); // +28 for suffix
  
  return isEnglish
    ? `${baseTitle} ${cityLocation} | Global Therapy Hub`
    : `${baseTitle} ${cityLocation} | Global Therapy Hub`;
};

/**
 * FASE 2: Generate city advantages content
 */
export const generateCityAdvantages = (
  city: City,
  language: CityLanguage
): string[] => {
  const cityName = city.name.replace(/\s+\d+$/, '').trim();
  const isEnglish = language === 'en';
  const mentalHealthStats = generateMentalHealthStats(city);
  
  const advantages = isEnglish ? CONTENT_VARIATIONS.en.cityAdvantages : CONTENT_VARIATIONS.es.cityAdvantages;
  
  return advantages.map(adv => 
    adv
      .replace(/{city}/g, cityName)
      .replace(/{country}/g, city.country)
      .replace(/{timezone}/g, city.timezone)
      .replace(/{therapists}/g, mentalHealthStats.therapistsPerCapita)
  );
};

/**
 * FASE 2: Generate therapeutic approaches list
 */
export const generateTherapeuticApproaches = (
  language: CityLanguage
): string[] => {
  const isEnglish = language === 'en';
  return isEnglish ? CONTENT_VARIATIONS.en.therapeuticApproaches : CONTENT_VARIATIONS.es.therapeuticApproaches;
};
